import React from 'react';

import { Badge, Box, HStack } from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons/linear';
import { Clock2, MessageQuestion } from '@nocowanie/icons/two-tone';

import { CssTooltip } from '@app/components/atoms/css-tooltip';
import {
    AccommodationTileBadgesProps,
    BadgeConfig,
} from '@app/components/molecules/accommodation-tile';
import { ACCOMMODATION_TILE } from '@app/consts';

const BADGE_STYLE = {
    fontSize: 'xs',
    lineHeight: 1,
    fontWeight: '600',
    gap: {
        base: 1,
        md: 1.5,
    },
    px: {
        base: 1,
        md: 2,
    },
    py: 1,
    borderRadius: 'sm',
    whiteSpace: 'wrap',
    textAlign: 'left',
    '--shadow-color': 'colors.gray.900',
};

const ICON_PROPS = {
    '--icon-stroke': 1,
    fontSize: 'sm',
    mt: -0.5,
};

export const AccommodationTileBadges = ({
    t,
    source,
    reservationType,
    isBestseller,
    listingPlacement,
}: AccommodationTileBadgesProps) => {
    const isInquiry = source === 'noc';
    const isRealTime = !isInquiry && reservationType === 'RealTime';

    const badgeConfigs: BadgeConfig[] = [];

    if (isInquiry) {
        badgeConfigs.push({
            id: 'inquiry',
            testId: 'acc-tile-badge-inquiry',
            variant: 'outline',
            colorPalette: 'gray',
            bg: 'blue.50',
            icon: <MessageQuestion fill={'orange.solid'} {...ICON_PROPS} />,
            text: t('badges.inquiry'),
            tooltipText: t('badges.tooltips.inquiry'),
            tooltipWidth: '320px',
        });
    } else if (isRealTime) {
        badgeConfigs.push({
            id: 'realtime',
            testId: 'acc-tile-badge-realtime',
            variant: 'solid',
            colorPalette: 'green',
            borderColor: 'gray.900',
            borderWidth: '1px',
            icon: <IconsLinear.Flash color={'gray.900'} fill={'orange.600'} {...ICON_PROPS} />,
            text: t('badges.realtime'),
            tooltipText: t('badges.tooltips.realtime'),
            tooltipWidth: '240px',
        });
    } else {
        badgeConfigs.push({
            id: 'onRequest',
            testId: 'acc-tile-badge-on-request',
            variant: 'outline',
            colorPalette: 'gray',
            bg: 'green.50',
            icon: <Clock2 fill={'orange.solid'} {...ICON_PROPS} />,
            text: t('badges.onRequest'),
            tooltipText: t('badges.tooltips.onRequest'),
            tooltipWidth: '348px',
        });
    }

    // Marketing badge placeholder - will be defined within CMS

    if (isBestseller) {
        badgeConfigs.push({
            id: 'bestseller',
            testId: 'acc-tile-badge-bestseller',
            variant: 'outline',
            colorPalette: 'gray',
            icon: <IconsLinear.Like1 accentFillColor={'orange.solid'} {...ICON_PROPS} />,
            text: t('badges.bestseller'),
        });
    }

    if (isInquiry && listingPlacement === 'Guarantee') {
        badgeConfigs.push({
            id: 'promoted',
            testId: 'acc-tile-badge-promoted',
            variant: 'outline',
            colorPalette: 'gray',
            icon: <IconsLinear.Star fill={'orange.solid'} {...ICON_PROPS} />,
            text: t('badges.promoted'),
        });
    }

    const badges = badgeConfigs.map(
        ({ id, testId, icon, text, tooltipText, tooltipWidth, ...badgeProps }) => {
            const createBadge = () => (
                <Badge data-testid={testId} {...badgeProps} {...BADGE_STYLE}>
                    {icon}
                    {text}
                </Badge>
            );

            return tooltipText ? (
                <React.Fragment key={`${id}-mobile`}>
                    <CssTooltip
                        content={tooltipText}
                        key={`${id}-desktop`}
                        zIndex={'overLinkOverlay'}
                        hideBelow={'md'}
                        alignSelf={'baseline'}
                        tooltipProps={{
                            w: tooltipWidth,
                            maxW: tooltipWidth,
                        }}
                    >
                        {createBadge()}
                    </CssTooltip>
                    <Box hideFrom={'md'} alignSelf={'baseline'}>
                        {createBadge()}
                    </Box>
                </React.Fragment>
            ) : (
                <Box key={id}>{createBadge()}</Box>
            );
        },
    );

    return (
        <HStack
            wrap={'wrap'}
            gap={{ base: 1, md: 2 }}
            lineHeight={1}
            data-testid={'acc-tile-badges'}
        >
            {badges.slice(0, ACCOMMODATION_TILE.MAX_MOBILE_BADGES)}
            {badges.slice(ACCOMMODATION_TILE.MAX_MOBILE_BADGES).map(badge => (
                <Box as={'span'} key={badge.key} hideBelow="md">
                    {badge}
                </Box>
            ))}
        </HStack>
    );
};
